import React, {
  useCallback, useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { MK3_COLORS } from 'features/Onboarding/components/mk3/styles';
import {
  SHEPHERD_Z_INDEX, TARGET_WAIT_TIMEOUT_MS, TARGET_POLL_INTERVAL_MS,
  CUTOUT_EASING, CUTOUT_MORPH_MS,
} from './constants';
import { ShepherdStepDefinition } from './types';

type Props = {
  step: ShepherdStepDefinition;
  index: number;
  total: number;
  onNext: () => void;
  onSkip: () => void;
};

type DisplayRect = { top: number; left: number; width: number; height: number };
type CardPos = {
  stepId: string; top: number; left: number; width: number; height: number;
};

const DIM = 'rgba(25, 0, 50, 0.55)';
// Classic giant-shadow dim: an oversized spread on the (rounded) hole frame
// paints everything outside it, so the dim always follows border-radius -
// pill-shaped and rounded-rect holes alike never leak a square corner.
const DIM_SHADOW = `0 0 0 4000px ${DIM}`;
const CARD_MAX_WIDTH = 300;
// How much of the viewport's edge a scrolled-to target must clear before we
// consider it "already in view" and skip the scrollIntoView call.
const SCROLL_HEADROOM = 80;
// Live intersection (item B): the displayed cutout is the raw target rect
// intersected with the viewport, inset 8px on all sides - so the hole hugs
// whatever portion of the target is actually visible instead of clamping to
// a fixed height ratio. As the page scrolls, the intersection shrinks/grows
// with it (the existing scroll listener re-resolves and snaps, no morph).
const VIEWPORT_INSET = 8;
// Belt-and-braces re-resolve cadence while a step is active: catches target
// nodes that React replaces without a size change (the ResizeObserver on the
// old node never fires again once it's detached).
const TARGET_RERESOLVE_INTERVAL_MS = 300;
// A real tour target is never this small; a smaller measurement means we're
// looking at a placeholder frame or a detached node (which measures 0x0).
const MIN_TARGET_SIZE = 4;
// Breathing room between the status bar (plus any stuck header row) and a
// target the tour has scrolled to.
const SCROLL_GAP = 12;

type SafeInsets = { top: number; bottom: number };
const NO_INSETS: SafeInsets = { top: 0, bottom: 0 };

// The status bar and home indicator areas. env(safe-area-inset-*) only exists
// in CSS, so it is read off a throwaway fixed element. 0 on web and Android,
// about 59/34 on a Dynamic Island iPhone. The pages pad themselves clear of
// the status bar (.mobile-screen-top) but scroll underneath it, so anything
// the tour scrolls to the top edge ends up under the clock unless it allows
// for this (TestFlight 1.149, iPhone 16 Pro).
const readSafeInsets = (): SafeInsets => {
  const probe = document.createElement('div');
  probe.className = 'shepherd-safe-probe';
  probe.style.cssText = 'position:fixed;top:0;left:0;width:0;height:0;visibility:hidden;pointer-events:none;'
    + 'padding-top:env(safe-area-inset-top, 0px);padding-bottom:env(safe-area-inset-bottom, 0px);';
  document.body.appendChild(probe);
  const cs = window.getComputedStyle(probe);
  const insets = { top: parseFloat(cs.paddingTop) || 0, bottom: parseFloat(cs.paddingBottom) || 0 };
  probe.remove();
  return insets;
};

// Sticky header rows (StickyHeaderRow, mobile only) stay pinned just under
// the status bar while the page scrolls, so a target scrolled to the top
// would sit under them too. A row that holds the target itself is ignored.
const stickyHeaderHeight = (target: HTMLElement): number => {
  let height = 0;
  document.querySelectorAll<HTMLElement>('.shr').forEach((row) => {
    if (!row.contains(target)) height = Math.max(height, row.offsetHeight);
  });
  return height;
};

const keyframes = `
@keyframes shepherdPulse {
  0% { transform: scale(.98); opacity: .9; }
  70% { transform: scale(1.06); opacity: 0; }
  100% { opacity: 0; }
}
@keyframes shepherdCardIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: none; }
}
`;

// Built rather than inlined as one long template literal to stay under max-len.
const FRAME_TRANSITION = ['top', 'left', 'width', 'height', 'border-radius']
  .map((prop) => `${prop} ${CUTOUT_MORPH_MS}ms ${CUTOUT_EASING}`)
  .join(', ');

const resolveTarget = (selectors: string[] | null): HTMLElement | null => {
  if (!selectors) return null;
  let found: HTMLElement | null = null;
  // .some() short-circuits at the first match, same as the priority-order for-loop it replaces.
  selectors.some((sel) => {
    found = document.querySelector<HTMLElement>(sel);
    return found !== null;
  });
  return found;
};

// A rect counts as "in view" once it clears SCROLL_HEADROOM top/bottom and
// sits fully within the viewport's width - used to decide whether a scroll
// is needed at all before measuring (item A.2).
const isFullyInView = (r: DOMRect, clearTop: number, insets: SafeInsets): boolean => (
  r.top >= Math.max(SCROLL_HEADROOM, clearTop)
  && r.bottom <= window.innerHeight - SCROLL_HEADROOM - insets.bottom
  && r.left >= 0
  && r.right <= window.innerWidth
);

// Item B: derive the DISPLAYED cutout rect as the intersection of the raw
// measured target rect with the viewport, inset 8px on all sides. A target
// that's partly scrolled off-screen gets a hole that hugs only its visible
// portion - shrinking as it scrolls further out, growing back as it scrolls
// in - rather than a fixed-ratio clamp anchored at the target's top.
// The top edge also stops at the status bar, so a tall target's hole never
// reaches up under the clock.
const intersectViewport = (r: DOMRect, insets: SafeInsets): DisplayRect => {
  const vh = window.innerHeight;
  const vw = window.innerWidth;
  const top = Math.max(r.top, insets.top + VIEWPORT_INSET);
  const bottom = Math.min(r.bottom, vh - VIEWPORT_INSET);
  const left = Math.max(r.left, VIEWPORT_INSET);
  const right = Math.min(r.right, vw - VIEWPORT_INSET);
  return {
    top,
    left,
    width: Math.max(0, right - left),
    height: Math.max(0, bottom - top),
  };
};

// Compare-before-set helper (item A.3): only treat a re-measure as a change
// worth a render if some field moved by more than 1px.
const rectsDiffer = (a: DOMRect | null, b: DOMRect, threshold = 1): boolean => {
  if (!a) return true;
  return (
    Math.abs(a.top - b.top) > threshold
    || Math.abs(a.left - b.left) > threshold
    || Math.abs(a.width - b.width) > threshold
    || Math.abs(a.height - b.height) > threshold
  );
};

export const ShepherdOverlay = ({
  step, index, total, onNext, onSkip,
}: Props) => {
  // rect + morph travel together: `morph` records whether THIS rect
  // application is the step's one elegant 550ms morph (first application for
  // the step) or a later correction that must snap instantly. Deciding at
  // application time - not render time - means unrelated re-renders (e.g.
  // cardPos settling) never flip an in-flight morph to a snap.
  const [rectState, setRectState] = useState<{ rect: DOMRect | null; morph: boolean }>(
    { rect: null, morph: true },
  );
  const [ready, setReady] = useState(false);
  const targetRef = useRef<HTMLElement | null>(null);
  const lastRectRef = useRef<DOMRect | null>(null);
  const lastMorphedStepRef = useRef<string | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [cardPos, setCardPos] = useState<CardPos | null>(null);
  // Re-read at the start of every step and on resize (rotation).
  const insetsRef = useRef<SafeInsets>(NO_INSETS);
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Latest-ref so the target-wait effect's timeout self-skip always calls the
  // current onNext without the poll restarting when the parent re-renders.
  const onNextRef = useRef(onNext);
  onNextRef.current = onNext;

  // Single entry point for every rect application. Full-dim (null) always
  // morphs (the collapse-to-center is a step transition, not a correction)
  // and does NOT consume the step's morph: the expand to a late-resolving
  // target still gets the 550ms treatment.
  const applyRect = useCallback((r: DOMRect | null) => {
    if (r === null) {
      setRectState({ rect: null, morph: true });
      return;
    }
    const morph = lastMorphedStepRef.current !== step.id;
    lastMorphedStepRef.current = step.id;
    setRectState({ rect: r, morph });
  }, [step.id]);

  // Resolve the step's target: try synchronously first (item A.1), and only
  // fall back to full-dim + polling when that sync attempt comes up empty.
  // A target found synchronously never nulls `rect` first, so the cutout
  // morphs DIRECTLY from wherever it currently sits to the new target -
  // no clear-to-center-then-expand in between.
  useEffect(() => {
    let cancelled = false;
    let pollId: number | null = null;
    targetRef.current = null;
    lastRectRef.current = null;
    insetsRef.current = readSafeInsets();

    if (step.target === null) {
      applyRect(null);
      setReady(true);
      return () => { cancelled = true; };
    }

    setReady(false);

    const commit = (r: DOMRect) => {
      lastRectRef.current = r;
      applyRect(r);
      setReady(true);
    };

    // Last rect the poll saw, so a target that is still moving can be told
    // apart from one that has settled.
    let seen: DOMRect | null = null;

    // Returns true once the target is found AND measures like a real element.
    // A degenerate rect (< MIN_TARGET_SIZE) means the node exists but hasn't
    // laid out yet (the ghost-tiles 4x4 first-frame bug) - keep polling.
    // waitForStill: only commit once two polls in a row agree on the rect
    // and the page has no loading skeletons left.
    // The poll path is the route-hop path ("Open it" landing on the category
    // page), where the page is still loading: groups and doqs arrive after
    // the first paint and push the target around. Showing the step on the
    // first sighting made the spotlight and card land, then jump, which read
    // as the tour racing on by itself (TestFlight 1.149).
    const tryResolve = (waitForStill: boolean): boolean => {
      const el = resolveTarget(step.target);
      if (!el) { seen = null; return false; }
      const raw = el.getBoundingClientRect();
      if (raw.width < MIN_TARGET_SIZE || raw.height < MIN_TARGET_SIZE) { seen = null; return false; }
      if (waitForStill && rectsDiffer(seen, raw)) { seen = raw; return false; }
      // The destination page is still filling in (its skeletons carry
      // data-tour-loading). Hold off until it has, so the spotlight lands
      // once, on the finished layout, rather than on the skeleton and then
      // again when the real groups and doqs push the target down.
      if (waitForStill && document.querySelector('[data-tour-loading]')) { seen = raw; return false; }
      targetRef.current = el;
      const insets = insetsRef.current;
      const clearTop = insets.top + stickyHeaderHeight(el) + SCROLL_GAP;
      if (isFullyInView(raw, clearTop, insets)) {
        commit(raw);
        return true;
      }
      // scroll-margin makes scrollIntoView stop short of the status bar and
      // any stuck header: 'start' lands the top just below them, and
      // 'center' centres the target in the clear band between them and the
      // bottom nav. Restored straight after, as the scroll is synchronous.
      const { scrollMarginTop, scrollMarginBottom } = el.style;
      el.style.scrollMarginTop = `${clearTop}px`;
      el.style.scrollMarginBottom = `${SCROLL_HEADROOM + insets.bottom}px`;
      el.scrollIntoView({
        block: raw.height > window.innerHeight * 0.6 ? 'start' : 'center',
        behavior: 'auto',
      });
      el.style.scrollMarginTop = scrollMarginTop;
      el.style.scrollMarginBottom = scrollMarginBottom;
      // behavior:'auto' scrolls synchronously, so the post-scroll rect can be
      // read immediately - no deferred frame. (A one-shot rAF here was lost to
      // StrictMode's mount/cleanup/mount cycle: the cleanup cancelled the rAF
      // after tryResolve had already returned true and stopped the poll,
      // leaving the step permanently unready.)
      const settled = el.getBoundingClientRect();
      commit(settled.width < MIN_TARGET_SIZE || settled.height < MIN_TARGET_SIZE ? raw : settled);
      return true;
    };

    if (tryResolve(false)) {
      return () => { cancelled = true; };
    }

    // Sync resolve failed: this is the only path that gives full-dim, and the
    // only place the self-skip timeout applies (initial resolution).
    applyRect(null);
    const startedAt = Date.now();
    pollId = window.setInterval(() => {
      if (cancelled) return;
      if (tryResolve(true)) {
        if (pollId !== null) window.clearInterval(pollId);
      } else if (Date.now() - startedAt > TARGET_WAIT_TIMEOUT_MS) {
        if (pollId !== null) window.clearInterval(pollId);
        // Found but never sat still: show it where it is now rather than
        // skipping a step whose target is plainly on screen.
        if (seen && tryResolve(false)) return;
        onNextRef.current(); // self-skip: never hang on a missing target
      }
    }, TARGET_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (pollId !== null) window.clearInterval(pollId);
    };
  }, [step.id, applyRect]);

  // Live tracking for the lifetime of the step. Never trusts a cached node:
  // every measure re-runs resolveTarget and reads the CURRENT element, because
  // the destination page's React tree can replace the target node as data
  // loads - the old node detaches (getBoundingClientRect goes 0x0) and a
  // ResizeObserver watching it never fires again. When the node changes, the
  // observer re-attaches; when resolution transiently fails (unmount during a
  // re-render), the last known rect is KEPT - no flash to full dim - and
  // tracking resumes when it reappears. A ~300ms interval backstops
  // replacements that change neither size nor trigger scroll/resize.
  useEffect(() => {
    if (!ready || step.target === null) return undefined;

    let rafId: number | null = null;
    let observed: HTMLElement | null = null;

    // Callback closes over scheduleMeasure lazily; RO only fires async, after
    // the effect body (and therefore scheduleMeasure's binding) has run.
    const ro = new ResizeObserver(() => scheduleMeasure());

    const measureNow = () => {
      const el = resolveTarget(step.target);
      if (!el) return; // transient unmount: keep the last rect, resume later
      if (el !== observed) {
        ro.disconnect();
        ro.observe(el);
        observed = el;
        targetRef.current = el;
      }
      const r = el.getBoundingClientRect();
      // Degenerate = placeholder/detached measurement, never a real target.
      if (r.width < MIN_TARGET_SIZE || r.height < MIN_TARGET_SIZE) return;
      if (rectsDiffer(lastRectRef.current, r)) {
        lastRectRef.current = r;
        applyRect(r);
      }
    };

    const scheduleMeasure = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        measureNow();
      });
    };

    if (targetRef.current) {
      observed = targetRef.current;
      ro.observe(observed);
    }
    window.addEventListener('scroll', scheduleMeasure, true);
    const reresolveId = window.setInterval(scheduleMeasure, TARGET_RERESOLVE_INTERVAL_MS);
    const onResize = () => {
      insetsRef.current = readSafeInsets();
      lastRectRef.current = null; // force a re-apply against the new insets
      scheduleMeasure();
    };
    window.addEventListener('resize', onResize);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', scheduleMeasure, true);
      window.clearInterval(reresolveId);
      if (rafId !== null) window.cancelAnimationFrame(rafId);
    };
  }, [ready, step.id, applyRect]);

  const { rect, morph } = rectState;
  const pad = step.pad ?? 6;
  const radius = step.radius ?? 18;
  const centered = step.target === null || rect === null;
  const insets = insetsRef.current;
  const displayedRect = rect ? intersectViewport(rect, insets) : null;

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const hole = centered
    ? {
      top: vh / 2, left: vw / 2, width: 0, height: 0,
    }
    : {
      top: displayedRect!.top - pad,
      left: displayedRect!.left - pad,
      width: displayedRect!.width + pad * 2,
      height: displayedRect!.height + pad * 2,
    };

  const frameRadius = centered ? 0 : radius;
  // Item A: the dim is the frame's own box-shadow, not four separate bands.
  // A giant 4000px spread painted on the (possibly rounded) frame always
  // follows its border-radius, so pill/rounded holes never leak a bright
  // square corner the way four rectangular bands did. Full-dim (centered)
  // still gets the giant shadow - the frame is just 0x0 at viewport center,
  // so the shadow's spread covers the entire screen either way.
  const boxShadow = centered
    ? DIM_SHADOW
    : `0 0 24px 6px rgba(171,114,237,.45), ${DIM_SHADOW}`;
  const frameStyle: React.CSSProperties = {
    position: 'absolute',
    top: hole.top,
    left: hole.left,
    width: hole.width,
    height: hole.height,
    borderRadius: frameRadius,
    border: centered ? '2px solid transparent' : '2px solid rgba(171,114,237,.9)',
    boxShadow,
    pointerEvents: 'none',
    transition: reduced || !morph ? 'none' : FRAME_TRANSITION,
  };

  // Item C: pure pixel card placement, measured against the displayed
  // (clamped) cutout rect. `cardPos.stepId` guards against applying a stale
  // position (computed for the previous step) to the freshly-mounted card.
  const cardWidth = Math.min(CARD_MAX_WIDTH, window.innerWidth - 24);
  useLayoutEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const height = el.offsetHeight;
    const width = cardWidth;
    let top: number;
    let left: number;
    if (centered) {
      left = (vw - width) / 2;
      top = (vh - height) / 2;
    } else {
      const d = displayedRect!;
      const holeTop = d.top - pad;
      const holeBottomPx = d.top + d.height + pad;
      const below = d.top + d.height / 2 < vh / 2;
      const rawTop = below ? holeBottomPx + 14 : holeTop - 14 - height;
      const rawLeft = d.left + d.width / 2 - width / 2;
      // The card keeps clear of the status bar and the home indicator too.
      const minTop = Math.max(8, insets.top + 8);
      const maxTop = vh - height - Math.max(8, insets.bottom + 8);
      top = Math.max(minTop, Math.min(rawTop, maxTop));
      left = Math.max(12, Math.min(rawLeft, vw - width - 12));

      // Item C.5: on cramped screens the clamp above can push the card back
      // over the hole. If it overlaps by more than half the card's height,
      // flip to whichever side (above/below) has more free room instead -
      // partial overlap is acceptable, off-screen never is.
      const overlapTop = Math.max(top, holeTop);
      const overlapBottom = Math.min(top + height, holeBottomPx);
      const overlap = Math.max(0, overlapBottom - overlapTop);
      if (overlap > height / 2) {
        const spaceAbove = holeTop - minTop;
        const spaceBelow = maxTop + height - holeBottomPx;
        top = spaceBelow >= spaceAbove
          ? Math.max(minTop, Math.min(holeBottomPx + 14, maxTop))
          : Math.max(minTop, Math.min(holeTop - 14 - height, maxTop));
      }
    }
    setCardPos({
      stepId: step.id, top, left, width, height,
    });
    // `rect` (not displayedRect, a derived value) is the real trigger for a
    // reposition; `centered`/`pad`/displayedRect are pure functions of
    // step.id + rect and don't need their own entries. `ready` matters for
    // centered steps: the card mounts on ready with rect still null, so
    // without it this effect would never re-run and the card would stay
    // parked at -9999.
  }, [step.id, rect, ready]);

  const positioned = ready && cardPos !== null && cardPos.stepId === step.id;

  const Vignette = step.vignette;

  return createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: SHEPHERD_Z_INDEX,
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Doqit tour"
    >
      <style>{keyframes}</style>

      {/* Item A: the hole-frame's own box-shadow paints the dim (see
          `boxShadow` above) - it always follows border-radius, so rounded
          and pill-shaped holes never leak a bright square corner the way
          four rectangular dim bands used to. */}
      <div style={frameStyle}>
        {!centered && !reduced && (
          <div
            style={{
              position: 'absolute',
              inset: -8,
              borderRadius: 'inherit',
              border: '2px solid rgba(171,114,237,.45)',
              animation: 'shepherdPulse 1.8s ease-out infinite',
            }}
          />
        )}
      </div>

      {ready && (
        <div
          key={step.id}
          ref={cardRef}
          style={{
            position: 'absolute',
            top: positioned ? cardPos!.top : -9999,
            left: positioned ? cardPos!.left : -9999,
            width: cardWidth,
            visibility: positioned ? 'visible' : 'hidden',
            background: MK3_COLORS.cardBg,
            border: `1px solid ${MK3_COLORS.cardBorder}`,
            borderRadius: 20,
            boxShadow: '2px 2px 5px rgba(24,0,100,0.15)',
            padding: '14px 16px 12px',
            fontFamily: 'Grift, sans-serif',
            animation: reduced ? 'none' : 'shepherdCardIn .3s ease-out both',
          }}
        >
          <div
            style={{
              fontSize: 10, fontWeight: 700, letterSpacing: 1.4, color: '#895BBE', marginBottom: 6,
            }}
          >
            {step.chip}
            {step.target !== null ? ` · ${index + 1} OF ${total}` : ''}
          </div>
          <div
            style={{
              fontWeight: 700, fontSize: 15, lineHeight: '20px', color: MK3_COLORS.heading,
            }}
          >
            {step.text}
          </div>
          {step.subtext && (
            <div
              style={{
                fontWeight: 500,
                fontSize: 13,
                lineHeight: '18px',
                color: MK3_COLORS.accent,
                marginTop: 6,
              }}
            >
              {step.subtext}
            </div>
          )}
          <div
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12,
            }}
          >
            {/* Item F: the dots give way first - minWidth:0 lets a flex item
                shrink below its content size (the default `auto` would keep
                it at full pip-row width and starve the buttons instead),
                overflow:hidden clips any pips that don't fit. */}
            <div
              style={{
                display: 'flex', gap: 4, minWidth: 0, overflow: 'hidden', flexShrink: 1,
              }}
              aria-hidden="true"
            >
              {Array.from({ length: total }, (_, k) => (
                <span
                  // eslint-disable-next-line react/no-array-index-key
                  key={k}
                  style={{
                    width: k === index ? 14 : 5,
                    height: 5,
                    borderRadius: 100,
                    background: k === index ? '#E516C5' : '#CDC4D6',
                    transition: 'all .3s',
                  }}
                />
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {!step.hideSkip && (
                <button
                  type="button"
                  onClick={onSkip}
                  data-cy="shepherd-skip"
                  style={{
                    background: 'none',
                    border: 0,
                    color: '#895BBE',
                    fontFamily: 'inherit',
                    fontWeight: 500,
                    fontSize: 13,
                    cursor: 'pointer',
                    padding: '6px 2px',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  Skip
                </button>
              )}
              <button
                type="button"
                onClick={onNext}
                data-cy="shepherd-next"
                style={{
                  background: '#AB72ED',
                  border: '1px solid #CDAAF4',
                  boxShadow: '2px 2px 5px rgba(24,0,100,0.15)',
                  borderRadius: 100,
                  color: '#F7F5F8',
                  fontFamily: 'inherit',
                  fontWeight: 700,
                  fontSize: 14,
                  letterSpacing: 0.5,
                  padding: '10px 22px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                {step.nextLabel ?? (index === total - 1 ? 'Done' : 'Next')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rendered after the card so vignette elements that deliberately sit
          on the card (the finale badge on its top edge) paint above it. All
          vignettes are pointerEvents:none and position clear of the copy. */}
      {ready && Vignette && (
        <Vignette
          targetRect={rect}
          cardRect={positioned ? {
            top: cardPos!.top, left: cardPos!.left, width: cardPos!.width, height: cardPos!.height,
          } : null}
          reduced={reduced}
        />
      )}
    </div>,
    document.body,
  );
};

export default ShepherdOverlay;
